import type { CategoriesApi } from "@/v2/api/metadata/categories";
import type { SportsApi } from "@/v2/api/metadata/sports";
import type { TeamsApi } from "@/v2/api/metadata/teams";
import type { TournamentsApi } from "@/v2/api/metadata/tournaments";
import type { DialogConfig } from "@/v2/framework/dialog/types";
import { buildAddSupplementalNamesPayload } from "@/v2/kits/supplemental-names";
import { teamGamesStatusOptions, teamStatusOptions } from "../../shared";
import { buildAddTournamentTeams } from "./tournaments/patch";

export function createAddDialog(options: {
  teamsApi: TeamsApi;
  sportsApi: SportsApi;
  categoriesApi: CategoriesApi;
  tournamentsApi: TournamentsApi;
}): DialogConfig {
  const { teamsApi, sportsApi, categoriesApi, tournamentsApi } = options;

  return {
    id: "add-team",
    title: "Add team",
    trigger: { type: "fab" },
    layout: { type: "popover" },
    structure: {
      type: "stepper",
      steps: [
        {
          id: "main",
          children: [
            {
              type: "group",
              name: "main",
              label: "Main",
              children: [
                {
                  type: "select-field",
                  name: "status",
                  label: "Status",
                  required: true,
                  options: teamStatusOptions,
                  defaultValue: 1,
                },
                {
                  type: "text-field",
                  name: "name",
                  label: "Name",
                  required: true,
                  placeholder: "Enter a name",
                },
                { type: "text-field", name: "info", label: "Info", placeholder: "Enter info" },
                {
                  type: "component",
                  component: () =>
                    import("@/v2/kits/supplemental-names/components/SupplementalNamesFields.vue"),
                  props: { mode: "add", entity: "TEAM" },
                },
                {
                  type: "component",
                  component: () => import("./tournaments/TeamTournamentsFields.vue"),
                  props: { mode: "add", sportsApi, categoriesApi, tournamentsApi },
                },
              ],
            },
            {
              type: "group",
              name: "prematchGame",
              label: "Pre-match",
              children: [
                {
                  type: "select-field",
                  name: "status",
                  label: "Status",
                  required: true,
                  options: teamGamesStatusOptions,
                  defaultValue: 1,
                },
              ],
            },
            {
              type: "group",
              name: "liveGame",
              label: "Live",
              children: [
                {
                  type: "select-field",
                  name: "status",
                  label: "Status",
                  required: true,
                  options: teamGamesStatusOptions,
                  defaultValue: 1,
                },
              ],
            },
          ],
        },
      ],
    },
    api: {
      submit: (data) => {
        const main = (data.main ?? {}) as Record<string, unknown>;
        const prematchGame = (data.prematchGame ?? {}) as Record<string, unknown>;
        const liveGame = (data.liveGame ?? {}) as Record<string, unknown>;
        const tournamentTeams = buildAddTournamentTeams(data);
        if (!tournamentTeams.length) {
          throw new Error("Assign the team to a tournament");
        }

        return teamsApi.create({
          type: 1,
          status: main.status,
          name: main.name,
          info: main.info === "" ? null : main.info,
          supplementalNames: buildAddSupplementalNamesPayload(data, "TEAM"),
          prematchGame: { status: prematchGame.status },
          liveGame: { status: liveGame.status },
          tournamentTeams,
        });
      },
    },
  };
}
